/**
 * Milestone celebration banner mock — Story 76.3a.
 *
 * Why this lives in shared-domain rather than `apps/shell/src/services/`:
 *   - `milestoneBannerClient.ts` is the file that has to short-circuit, and
 *     it lives in shared-domain. Importing from `apps/shell` would create a
 *     reverse layering dependency.
 *   - The 76.2a precedent (`notificationMock.ts` in `apps/shell/`) works
 *     because the notification client uses raw `fetch` and the mock installs
 *     a `window.fetch` interceptor at app boot. The milestone-banner client
 *     uses `axiosInstance` (correct: this is a tenant-scoped domain that
 *     needs the `X-Resource-Tenant-Id` header auto-injected by the axios
 *     request interceptor); the same fetch-interceptor trick does not apply.
 *
 * Behaviour:
 *   - Mocks are **on by default in non-production builds** so devs can iterate
 *     against the banner without flipping any flag. Production builds always
 *     get `false`.
 *   - The fixture set starts with three pre-seeded banners; `pushMockBanner()`
 *     prepends a fresh fake banner to the front of the working set (most-
 *     recent-first to mirror the BE ordering). The header "Push mock banner"
 *     button in `AttentionFeed` calls this in dev.
 *   - Acknowledgement is recorded in-memory so the dismiss flow is realistic
 *     (clicking X removes the banner from subsequent fetches in the same
 *     session). Reload to reset.
 *
 * Per `feedback_mocking_scope.md`: this toggle only affects the milestone-
 * banner mock. No sweep-enable of unrelated mocks.
 */

import type { MilestoneBannerItem, MilestoneBannerResponse } from '../types/models/MilestoneBanner';

/**
 * Manual toggle — flip to `true` while developing against the banner stack,
 * back to `false` before sign-off. Same pattern as `USE_MOCK_NOTIFICATIONS`
 * in `notificationMock.ts`. Per `feedback_mocking_scope.md`, only flip this
 * flag; do not sweep-enable unrelated mocks.
 *
 * When `true`, `AttentionFeed`'s header surfaces a "Push mock banner" button
 * (visible only while this flag is on) that prepends a fresh fake banner to
 * the working set via `pushMockBanner()`.
 */
export const USE_MOCK_MILESTONE_BANNERS = false;

const MOCK_KUDOS_URL = 'https://app.hibob.com/kudos';

// Mutable in-memory fixture set. Mutations happen in dev only.
const fixtures: MilestoneBannerItem[] = [
  {
    milestoneId: 'mock-milestone-1',
    milestoneName: 'MVP Launch',
    projectId: 'mock-project-1',
    projectName: 'Helios Platform',
    completedAt: '2026-05-13T09:30:00Z',
  },
  {
    milestoneId: 'mock-milestone-2',
    milestoneName: 'Beta Cutover',
    projectId: 'mock-project-2',
    projectName: 'Customer Portal',
    completedAt: '2026-05-12T16:45:00Z',
  },
  {
    milestoneId: 'mock-milestone-3',
    milestoneName: 'Auth Hardening',
    projectId: 'mock-project-3',
    projectName: 'Identity Service',
    completedAt: '2026-05-11T11:00:00Z',
  },
];

// Per-session acknowledgements so the dismiss flow looks real in dev.
const acknowledged = new Set<string>();

// Source for the rotating fake names the push button cycles through.
const PUSH_NAMES = [
  { milestoneName: 'Search Indexing', projectName: 'Discovery Service' },
  { milestoneName: 'Onboarding v2', projectName: 'Growth Platform' },
  { milestoneName: 'Payments Migration', projectName: 'Billing' },
  { milestoneName: 'Reporting Refresh', projectName: 'Analytics' },
  { milestoneName: 'Mobile Beta', projectName: 'Mobile App' },
  { milestoneName: 'Dark Mode Rollout', projectName: 'Web Client' },
];

let pushCounter = 0;

export async function mockFetchMilestoneBanners(): Promise<MilestoneBannerResponse> {
  // Up to 3 banners returned (matches BE 76.3b AC). Acknowledged ones are
  // filtered first so the cap reflects the user-visible state.
  const visible = fixtures.filter((b) => !acknowledged.has(b.milestoneId));
  return Promise.resolve({ items: visible.slice(0, 3), kudosUrl: MOCK_KUDOS_URL });
}

export async function mockAcknowledgeMilestoneBanner(milestoneId: string): Promise<void> {
  acknowledged.add(milestoneId);
  return Promise.resolve();
}

/**
 * Dev affordance — prepend a fresh fake banner to the working set so a
 * subsequent fetch returns it at the top of the stack (most-recent-first,
 * mirroring the BE ordering).
 */
export function pushMockBanner(): MilestoneBannerItem {
  const naming = PUSH_NAMES[pushCounter % PUSH_NAMES.length];
  pushCounter += 1;
  const id = `mock-pushed-${Date.now()}-${pushCounter}`;
  const item: MilestoneBannerItem = {
    milestoneId: id,
    milestoneName: naming.milestoneName,
    projectId: `mock-pushed-project-${pushCounter}`,
    projectName: naming.projectName,
    completedAt: new Date().toISOString(),
  };
  fixtures.unshift(item);
  return item;
}

/** Test-only: reset the per-session acknowledgements and pushed banners. */
export function resetMilestoneBannerMockState(): void {
  acknowledged.clear();
  // Pushed banners are prepended via `unshift`, so trim from the front to
  // restore the original three seeds.
  if (fixtures.length > 3) fixtures.splice(0, fixtures.length - 3);
  pushCounter = 0;
}
